import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useState } from "react";
import Searchbox from "components/fields/Searchbox";
import { LogsRowObj } from "..";
import moment from "moment";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import Pagination from "components/pagination";
import ShortTruncateCopy from "components/common/ShortTruncateCopy";
import FilterModal from "./FilterModal";
import InfoModal from "./infoModal";

const columnHelper = createColumnHelper<LogsRowObj>();

function LogsTable(props: {
  tableData: any;
  fetchLogs: () => void;
  onValueChange: (value: string) => void;
  roleData?: any;
  isLoading: boolean;
  page: number;
  setPage: any;
  totalpage: number;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  setPageSize: any;
  clients: any[];
  filterData: () => void;
  filter: any;
  setFilter: (e: any) => void;
  setLocalFilters?: (e: any) => void;
  setClearClick: () => void;
  localFilters?: any;
  isAllDataReady?: any;
}) {
  const {
    filter, setFilter, setClearClick, setLocalFilters, localFilters,
    filterData, tableData, onValueChange, isLoading, page, setPage,
    totalpage, totalItems, currentPage, pageSize, setPageSize, clients, isAllDataReady,
  } = props;

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState(() => [...(tableData ?? [])]);

  useEffect(() => {
    setData(tableData?.map((item: any) => ({ ...item })) ?? []);
  }, [tableData]);

  const handleValueChange = (e: any) => onValueChange(e);
  const hasActiveFilter = filter?.client?.length > 0 || filter?.dates;

  const columns = [
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: () => <span className="lgt-th">Date & Time</span>,
      cell: (info: any) => (
        <div className="lgt-date-cell">
          <div className="lgt-date-icon">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <span className="lgt-date">{moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss")}</span>
        </div>
      ),
      enableSorting: true,
    }),
    columnHelper.accessor("clientId", {
      id: "clientId",
      header: () => <span className="lgt-th">Client ID</span>,
      cell: (info) => (
        <div className="lgt-id-cell">
          <ShortTruncateCopy info={info?.getValue()} showCopy={true} />
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.clientId || "";
        const b = rowB.original.clientId || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.createdAt).localeCompare(String(rowB.original.createdAt));
      },
      enableSorting: true,
    }),
    columnHelper.accessor("clientName", {
      id: "clientName",
      header: () => <span className="lgt-th">Client Name</span>,
      cell: (info: any) => (
        <div className="lgt-client-cell">
          <div className="lgt-client-avatar">
            {info?.getValue()?.charAt(0)?.toUpperCase() || "C"}
          </div>
          <span className="lgt-client-name">{info?.getValue() || "—"}</span>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.clientName || "";
        const b = rowB.original.clientName || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.createdAt).localeCompare(String(rowB.original.createdAt));
      },
      enableSorting: true,
    }),
    columnHelper.accessor("endpoint", {
      id: "endpoint",
      header: () => <span className="lgt-th">API Request</span>,
      cell: (info: any) => (
        <InfoModal info={info.row.original} type="request" />
      ),
      enableSorting: false,
    }),
    columnHelper.accessor("responseData", {
      id: "responseData",
      header: () => <span className="lgt-th">API Response</span>,
      cell: (info: any) => (
        <InfoModal info={info.row.original} type="response" />
      ),
      enableSorting: false,
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="lgt-card">

      {/* ── Header ── */}
      <div className="lgt-header">
        <div className="lgt-header-left">
          <div className="lgt-header-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
          </div>
          <div>
            <h3 className="lgt-title">Request Logs</h3>
            <p className="lgt-subtitle">
              {totalItems > 0 ? `${totalItems} total log${totalItems !== 1 ? "s" : ""}` : "No logs found"}
            </p>
          </div>
        </div>

        <div className="lgt-header-right">
          {/* {hasActiveFilter && (
            <div className="lgt-filter-badge">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              Filters active
            </div>
          )} */}
          <FilterModal
            clients={clients}
            setPage={setPage}
            filter={filter}
            setFilter={setFilter}
            filterData={filterData}
            setLocalFilters={setLocalFilters}
            localFilters={localFilters}
            setClearClick={setClearClick}
            isAllDataReady={isAllDataReady}
          />
          <Searchbox onSearch={handleValueChange} />
        </div>
      </div>

      {/* ── Table ── */}
      <div className="lgt-table-wrap">
        {isLoading ? (
          <div className="lgt-loading">
            <div className="lgt-spinner" />
            <p className="lgt-loading-txt">Loading request logs…</p>
          </div>
        ) : (
          <table className="lgt-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th
                      key={h.id}
                      colSpan={h.colSpan}
                      onClick={h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined}
                      className={`lgt-thead-th${h.column.getCanSort() ? " lgt-thead-th--sort" : ""}`}
                    >
                      <div className="lgt-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="lgt-sort-icon">
                            {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> :
                             h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> :
                             <RiArrowUpDownFill size={11} />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, ri) => (
                  <tr key={row.id} className={`lgt-tbody-tr${ri % 2 !== 0 ? " lgt-tbody-tr--alt" : ""}`}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="lgt-tbody-td">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5}>
                    <div className="lgt-empty">
                      <div className="lgt-empty-icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                      </div>
                      <p className="lgt-empty-title">No request logs found</p>
                      <p className="lgt-empty-sub">Try adjusting your search or filters</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ── */}
      <div className="lgt-pagination-wrap">
        <Pagination
          setPage={setPage} page={page} totalpage={totalpage}
          currentPage={currentPage} pageSize={pageSize}
          setPageSize={setPageSize} arraySize={[50, 100, 200]}
        />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .lgt-card {
          font-family:'Plus Jakarta Sans',sans-serif;
          background:#fff; border:1px solid #e2e8f0; border-radius:18px;
          overflow:hidden; box-shadow:0 2px 8px rgba(15,23,42,0.06);
        }

        /* Header */
        .lgt-header { padding:20px 24px 16px; border-bottom:1px solid #f1f5f9; display:flex; align-items:center; justify-content:space-between; gap:14px; flex-wrap:wrap; }
        .lgt-header-left { display:flex; align-items:center; gap:12px; }
        .lgt-header-icon { width:40px; height:40px; border-radius:11px; background:linear-gradient(135deg,#f0f9ff,#e0f2fe); border:1.5px solid #bae6fd; display:flex; align-items:center; justify-content:center; color:#0284c7; flex-shrink:0; }
        .lgt-title { font-size:15px; font-weight:700; color:#0f172a; letter-spacing:-0.2px; margin-bottom:2px; line-height:22px; }
        .lgt-subtitle { font-size:12px; color:#94a3b8; font-weight:500; }
        .lgt-header-right { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }

        .lgt-filter-badge { display:inline-flex; align-items:center; gap:5px; padding:5px 11px; border-radius:8px; font-size:11.5px; font-weight:700; background:linear-gradient(135deg,#f0f9ff,#e0f2fe); border:1.5px solid #bae6fd; color:#0284c7; white-space:nowrap; }

        /* Table */
        .lgt-table-wrap { overflow-x:auto; }
        .lgt-table-wrap::-webkit-scrollbar { height:4px; }
        .lgt-table-wrap::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }
        .lgt-table { width:100%; border-collapse:collapse; }

        .lgt-thead-th { padding:12px 16px 10px; background:#f8fafc; border-bottom:1.5px solid #e2e8f0; text-align:left; white-space:nowrap; user-select:none; }
        .lgt-thead-th--sort { cursor:pointer; }
        .lgt-thead-th--sort:hover { background:#f1f5f9; }
        .lgt-th-inner { display:flex; align-items:center; gap:5px; }
        .lgt-th { font-size:11.5px; font-weight:700; color:#374151; text-transform:uppercase; letter-spacing:0.5px; }
        .lgt-sort-icon { color:#94a3b8; display:flex; align-items:center; }
        .lgt-thead-th--sort:hover .lgt-sort-icon { color:#0284c7; }

        .lgt-tbody-tr { transition:background .12s; }
        .lgt-tbody-tr:hover { background:#f8fafc; }
        .lgt-tbody-tr--alt { background:#fafbfc; }
        .lgt-tbody-tr--alt:hover { background:#f1f5f9; }
        .lgt-tbody-td { padding:12px 16px; border-bottom:1px solid #f1f5f9; vertical-align:middle; }

        /* Cells */
        .lgt-date-cell { display:flex; align-items:center; gap:8px; }
        .lgt-date-icon { width:26px; height:26px; border-radius:7px; background:linear-gradient(135deg,#f0f9ff,#e0f2fe); border:1.5px solid #bae6fd; display:flex; align-items:center; justify-content:center; color:#0284c7; flex-shrink:0; }
        .lgt-date { font-size:12.5px; font-weight:600; color:#0f172a; white-space:nowrap; }

        .lgt-id-cell { font-size:12.5px; font-weight:600; color:#475569; }

        .lgt-client-cell { display:flex; align-items:center; gap:8px; }
        .lgt-client-avatar { width:28px; height:28px; border-radius:7px; flex-shrink:0; background:linear-gradient(135deg,#f0f9ff,#e0f2fe); border:1.5px solid #bae6fd; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:800; color:#0284c7; }
        .lgt-client-name { font-size:13px; font-weight:600; color:#0f172a; white-space:nowrap; }

        /* Empty */
        .lgt-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:56px 24px; gap:10px; }
        .lgt-empty-icon { width:52px; height:52px; border-radius:13px; background:#f8fafc; border:1.5px solid #e2e8f0; display:flex; align-items:center; justify-content:center; color:#cbd5e1; }
        .lgt-empty-title { font-size:14px; font-weight:700; color:#64748b; }
        .lgt-empty-sub { font-size:12.5px; color:#94a3b8; font-weight:500; text-align:center; }

        /* Loading */
        .lgt-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:56px; gap:12px; }
        .lgt-spinner { width:28px; height:28px; border:3px solid #e2e8f0; border-top-color:#0284c7; border-radius:50%; animation:lgtSpin .7s linear infinite; }
        @keyframes lgtSpin { to { transform:rotate(360deg); } }
        .lgt-loading-txt { font-size:13px; color:#94a3b8; font-weight:500; }

        /* Pagination */
        .lgt-pagination-wrap { padding:12px 16px; border-top:1px solid #f1f5f9; background:#fafbfc; }

        @media (max-width:700px) {
          .lgt-header { flex-direction:column; align-items:flex-start; }
          .lgt-header-right { width:100%; }
        }
      `}</style>
    </div>
  );
}

export default LogsTable;